import React, { useState } from "react";

import { Info } from "lucide-react";

import {
  Field,
  ghostButton,
  inputClass,
  Panel,
  Pill,
  smallButton,
} from "./parts";
import { S } from "./strings";
import { TemplateChange } from "./templateDiff";
import Button from "../../Components/Button";
import { useAppDispatch } from "../../Store/Hooks";
import {
  followDefaultTemplate,
  publishTemplate,
  rollbackTemplate,
  TemplateBusy,
  TemplateScopeDetail,
  TemplateVersion,
} from "../../Store/Reducers/AccountPlanTemplateSlice";

interface Props {
  scope: string;
  detail: TemplateScopeDetail;
  changes: TemplateChange[];
  isDefaultScope: boolean;
  target: string;
  busy: TemplateBusy;
}

const KIND_LABEL: Record<string, { label: string; className: string }> = {
  add: { label: S.added, className: "text-green-700" },
  edit: { label: S.changed, className: "text-amber-700" },
  order: { label: S.changed, className: "text-amber-700" },
  del: { label: S.removed, className: "text-red-600" },
};

const ACTION_LABEL: Record<TemplateVersion["action"], string> = {
  publish: S.actionPublish,
  rollback: S.actionRollback,
  follow_default: S.actionFollow,
};

const describe = (c: TemplateChange): string => {
  const what = c.what === "question" ? S.question : S.section;
  if (c.kind === "order") return S.orderChanged(what);
  const fallback =
    c.what === "question" ? S.untitledQuestion : S.untitledSection;
  return `${what}: ${c.label || fallback}`;
};

const MIN_REASON = 10;

/** The pasted signed eval result: {payload: {eval_id…}, sig}. Null when unreadable. */
const parseAttestation = (
  text: string
): { value: Record<string, unknown> | null; evalId: string } | null => {
  if (!text.trim()) return null;
  try {
    const parsed = JSON.parse(text);
    const payload = parsed?.payload;
    if (
      payload &&
      typeof payload === "object" &&
      typeof parsed.sig === "string"
    )
      return { value: parsed, evalId: String(payload.eval_id ?? "") };
  } catch {
    // fall through: unreadable
  }
  return { value: null, evalId: "" };
};

const formatWhen = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString() : "";

const PublishPanel: React.FC<Props> = ({
  scope,
  detail,
  changes,
  isDefaultScope,
  target,
  busy,
}) => {
  const dispatch = useAppDispatch();
  const [note, setNote] = useState("");
  const [evalText, setEvalText] = useState("");
  const [forceReason, setForceReason] = useState("");
  const [confirming, setConfirming] = useState<"eval" | "force" | null>(null);
  const attestation = parseAttestation(evalText);
  const reasonOk = forceReason.trim().length >= MIN_REASON;
  const liveId = detail.live.follows_default
    ? null
    : (detail.live.version?.id ?? null);
  // The tenant's own newest row, which may be a "follow the default" marker.
  const newestOwn = detail.history[0]?.id ?? null;

  const publish = async (mode: "eval" | "force") => {
    const result = await dispatch(
      publishTemplate(
        mode === "eval"
          ? { scope, note, attestation: attestation?.value }
          : { scope, note, forceReason }
      )
    );
    if (publishTemplate.fulfilled.match(result)) {
      setNote("");
      setEvalText("");
      setForceReason("");
    }
    setConfirming(null);
  };

  const confirmRow = (mode: "eval" | "force", label: string, ready: boolean) =>
    confirming === mode ? (
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant={mode === "force" ? "danger" : "primary"}
          loading={busy === "publish"}
          disabled={busy !== null}
          onClick={() => void publish(mode)}
        >
          {S.confirmPublish(target)}
        </Button>
        <button
          type="button"
          className={ghostButton}
          onClick={() => setConfirming(null)}
        >
          {S.cancel}
        </button>
      </div>
    ) : (
      <div>
        <Button
          variant={mode === "force" ? "outline-primary" : "primary"}
          disabled={changes.length === 0 || !ready || busy !== null}
          onClick={() => setConfirming(mode)}
        >
          {label}
        </Button>
      </div>
    );

  return (
    <div className="grid gap-4">
      <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
        <Info size={14} className="mt-px shrink-0" />
        <span>{S.notYetUsed}</span>
      </div>

      <Panel title={S.changesTitle} hint={S.changesHint(target)}>
        <div className="px-4 py-3">
          {changes.length === 0 ? (
            <p className="text-sm text-gray-500">{S.noChanges}</p>
          ) : (
            <ul className="m-0 list-none p-0">
              {changes.map((c, i) => (
                <li
                  key={`${c.kind}-${c.what}-${i}`}
                  className="grid grid-cols-[72px_minmax(0,1fr)] gap-2 border-t border-gray-100 py-1.5 text-sm first:border-t-0"
                >
                  <span
                    className={`text-[11px] font-semibold uppercase tracking-wide ${KIND_LABEL[c.kind].className}`}
                  >
                    {KIND_LABEL[c.kind].label}
                  </span>
                  <span className="break-words text-gray-800">
                    {describe(c)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Panel>

      <Panel
        title={S.publishTitle}
        hint={isDefaultScope ? S.defaultFanout : undefined}
      >
        <div className="grid gap-4 px-4 py-3">
          <Field label={S.noteLabel} htmlFor="tpl-publish-note">
            <input
              id="tpl-publish-note"
              type="text"
              maxLength={500}
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder={S.notePlaceholder}
              className={inputClass}
            />
          </Field>
          <Field
            label={S.evalLabel}
            help={S.evalHelp}
            htmlFor="tpl-publish-eval"
          >
            <textarea
              id="tpl-publish-eval"
              rows={3}
              value={evalText}
              onChange={e => setEvalText(e.target.value)}
              placeholder={S.evalPlaceholder}
              className={`${inputClass} font-mono text-xs`}
            />
          </Field>
          {evalText.trim() && (
            <p
              className={`text-xs ${attestation?.value ? "text-green-700" : "text-red-600"}`}
            >
              {attestation?.value
                ? S.evalRead(attestation.evalId)
                : S.evalUnreadable}
            </p>
          )}
          {confirmRow("eval", S.publishFor(target), !!attestation?.value)}

          <details className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
            <summary className="cursor-pointer text-sm font-medium text-amber-800">
              {S.forceTitle}
            </summary>
            <div className="mt-3 grid gap-3">
              <p className="text-xs text-amber-800">{S.forceHint}</p>
              <Field
                label={S.forceReasonLabel}
                htmlFor="tpl-publish-force-reason"
              >
                <textarea
                  id="tpl-publish-force-reason"
                  rows={2}
                  maxLength={500}
                  value={forceReason}
                  onChange={e => setForceReason(e.target.value)}
                  placeholder={S.forceReasonPlaceholder}
                  className={inputClass}
                />
              </Field>
              {confirmRow("force", S.forcePublish, reasonOk)}
            </div>
          </details>
        </div>
      </Panel>

      {!isDefaultScope && !detail.live.follows_default && (
        <Panel title={S.followDefault} hint={S.followDefaultHint}>
          <div className="px-4 py-3">
            <button
              type="button"
              className={smallButton}
              disabled={busy !== null}
              onClick={() => void dispatch(followDefaultTemplate(scope))}
            >
              {S.followDefault}
            </button>
          </div>
        </Panel>
      )}

      <Panel title={S.historyTitle} hint={S.historyHint}>
        <div className="px-4 py-2">
          {detail.history.length === 0 ? (
            <p className="py-1 text-sm text-gray-500">
              {S.noHistory(isDefaultScope)}
            </p>
          ) : (
            <ul className="m-0 list-none p-0">
              {detail.history.map(v => {
                const isLive =
                  v.id === liveId || (v.follows_default && v.id === newestOwn);
                return (
                  <li
                    key={v.id}
                    className="flex flex-wrap items-start justify-between gap-2 border-t border-gray-100 py-2 first:border-t-0"
                  >
                    <span className="min-w-0">
                      <span className="flex flex-wrap items-center gap-2 text-sm text-gray-900">
                        <span className="font-mono text-xs tabular-nums text-gray-500">
                          v{v.version_no}
                        </span>
                        {ACTION_LABEL[v.action]}
                        {isLive && <Pill tone="green">{S.liveNow}</Pill>}
                        {v.action === "publish" && v.eval_id && (
                          <Pill tone="blue">{S.evaluated}</Pill>
                        )}
                        {v.action === "publish" && v.forced && (
                          <Pill tone="amber">{S.noEval}</Pill>
                        )}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {v.by} · {formatWhen(v.at)}
                      </span>
                      {v.forced && v.force_reason && (
                        <span className="mt-0.5 block break-words text-xs text-amber-700">
                          {S.forcedBecause(v.force_reason)}
                        </span>
                      )}
                      {v.note && (
                        <span className="mt-0.5 block break-words text-xs text-gray-600">
                          {v.note}
                        </span>
                      )}
                    </span>
                    {!isLive && v.id !== newestOwn && (
                      <button
                        type="button"
                        className={smallButton}
                        disabled={busy !== null}
                        onClick={() =>
                          void dispatch(
                            rollbackTemplate({ scope, versionId: v.id })
                          )
                        }
                      >
                        {S.rollBackTo(v.version_no)}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Panel>
    </div>
  );
};

export default PublishPanel;
